/**
 * Cart Drawer - Show Only 2 Cheapest Products
 * Customized for your Shopify theme
 * This script filters the upsell products to show only the 2 cheapest ones
 */

(function() {
  'use strict';

  // Configuration
  const CONFIG = {
    // Container for all upsell products
    upsellContainerId: '#cart-upsell-drawer',
    
    // Individual product card selector (based on mini-card-product snippet)
    productCardSelector: '.mini-card-product, [data-product-card]',
    
    // Alternative selectors if above doesn't work
    fallbackSelectors: [
      '.splide__slide',
      '.x-splide-slide',
      '[data-product-id]'
    ],
    
    // Price selector within each product
    priceSelectors: [
      '.price',
      '[data-price]',
      '.product-price',
      '.mini-card-product__price'
    ],
    
    // Number of cheapest products to show
    productsToShow: 6,
    
    // Debug mode - set to true to see what's happening
    debug: false
  };

  /**
   * Extract numeric price from text
   */
  function extractPrice(text) {
    if (!text) return Infinity;
    
    // Handle LE currency format specifically
    // Match patterns like "LE 390.00" or "LE390.00" or "390.00"
    const matches = text.match(/\d+[.,]?\d*/g);
    
    if (!matches || matches.length === 0) {
      if (CONFIG.debug) {
        console.warn('No price found in text:', text);
      }
      return Infinity;
    }
    
    // Take the last number (usually the actual price, not the compare-at price)
    const priceText = matches[matches.length - 1].replace(',', '');
    const price = parseFloat(priceText);
    
    if (CONFIG.debug) {
      console.log('Price extracted:', price, 'from:', text);
    }
    
    return isNaN(price) ? Infinity : price;
  }

  /**
   * Get price from a product element
   */
  function getProductPrice(productElement) {
    // Try each price selector
    for (const selector of CONFIG.priceSelectors) {
      const priceElement = productElement.querySelector(selector);
      if (priceElement) {
        const priceText = priceElement.textContent || priceElement.innerText;
        const price = extractPrice(priceText);
        if (price !== Infinity) {
          return price;
        }
      }
    }
    
    if (CONFIG.debug) {
      console.warn('Could not find price for product:', productElement);
    }
    
    return Infinity;
  }

  /**
   * Find product cards using multiple selector strategies
   */
  function findProductCards(container) {
    let products = [];
    
    // Try main selector
    products = Array.from(container.querySelectorAll(CONFIG.productCardSelector));
    
    // If no products found, try fallback selectors
    if (products.length === 0) {
      for (const selector of CONFIG.fallbackSelectors) {
        products = Array.from(container.querySelectorAll(selector));
        if (products.length > 0) {
          if (CONFIG.debug) {
            console.log('Found products using fallback selector:', selector);
          }
          break;
        }
      }
    }
    
    return products;
  }

  /**
   * Main function to filter and show only cheapest products
   */
  function filterCheapestProducts() {
    const container = document.querySelector(CONFIG.upsellContainerId);
    
    if (!container) {
      if (CONFIG.debug) {
        console.log('Upsell container not found:', CONFIG.upsellContainerId);
      }
      return;
    }

    // Find all product cards
    const productCards = findProductCards(container);
    
    if (productCards.length === 0) {
      if (CONFIG.debug) {
        console.log('No product cards found');
      }
      return;
    }

    if (CONFIG.debug) {
      console.log('Found', productCards.length, 'product cards');
    }

    // If there are 6 or fewer products, show them all but still sort them
    const shouldFilter = productCards.length > CONFIG.productsToShow;

    // Create array of products with their prices
    const productsWithPrices = productCards.map(card => ({
      element: card,
      price: getProductPrice(card)
    }));

    // Sort by price (ascending - cheapest first)
    productsWithPrices.sort((a, b) => a.price - b.price);

    if (CONFIG.debug) {
      console.log('Sorted products by price:', productsWithPrices.map(p => ({
        price: p.price,
        element: p.element
      })));
    }

    // Reorder products in the DOM based on sorted order
    const fragment = document.createDocumentFragment();
    
    if (shouldFilter) {
      // Hide all products first, then show only cheapest
      productsWithPrices.forEach((product, index) => {
        if (index < CONFIG.productsToShow) {
          product.element.style.display = '';
          fragment.appendChild(product.element);
        } else {
          product.element.style.display = 'none';
        }
      });
    } else {
      // Show all products but in sorted order
      productsWithPrices.forEach(product => {
        product.element.style.display = '';
        fragment.appendChild(product.element);
      });
    }
    
    // Append sorted products back to container
    container.appendChild(fragment);

    if (CONFIG.debug) {
      console.log('Filtering complete. Showing', Math.min(CONFIG.productsToShow, productCards.length), 'cheapest products (sorted)');
    }
  }

  /**
   * Initialize and setup observers
   */
  function init() {
    if (CONFIG.debug) {
      console.log('Initializing cart drawer filter...');
    }

    // Wait a bit for cart to load, then retry if needed
    setTimeout(() => {
      filterCheapestProducts();
      
      // Retry after another delay in case products load slowly
      setTimeout(filterCheapestProducts, 1000);
    }, 500);

    // Watch for changes in the cart drawer
    const observer = new MutationObserver((mutations) => {
      let shouldFilter = false;
      
      mutations.forEach((mutation) => {
        // Check if upsell products were added or modified
        if (mutation.addedNodes.length > 0 || 
            mutation.target.id === 'cart-upsell-drawer' ||
            mutation.target.closest('#mini-cart-upsell')) {
          shouldFilter = true;
        }
      });
      
      if (shouldFilter) {
        setTimeout(filterCheapestProducts, 200);
      }
    });

    // Observe the entire cart drawer
    const cartDrawer = document.querySelector('#CartDrawer, #update-cart');
    if (cartDrawer) {
      observer.observe(cartDrawer, {
        childList: true,
        subtree: true,
        attributes: false
      });
      
      if (CONFIG.debug) {
        console.log('Observer attached to cart drawer');
      }
    }

    // Also filter when cart opens
    document.addEventListener('click', (e) => {
      // Check if cart button was clicked
      if (e.target.closest('[href*="cart"]') || 
          e.target.closest('.cart-icon') ||
          e.target.closest('[data-cart-open]')) {
        setTimeout(filterCheapestProducts, 800);
      }
    });

    // Listen for Alpine.js cart events if available
    if (typeof Alpine !== 'undefined') {
      document.addEventListener('alpine:initialized', () => {
        setTimeout(filterCheapestProducts, 500);
      });
    }

    // Custom events from your theme
    document.addEventListener('cart:updated', () => {
      setTimeout(filterCheapestProducts, 500);
    });
    
    document.addEventListener('cart:refresh', () => {
      setTimeout(filterCheapestProducts, 500);
    });
  }

  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Also run when page is fully loaded
  window.addEventListener('load', () => {
    setTimeout(filterCheapestProducts, 1000);
  });

})();